/**
 * Shared upsell module -- ONE offer, TWO placements (product page + cart drawer).
 *
 * Deliberately does NOT patch the theme's cart JS. This theme is
 * Horizon-generation: the cart drawer lives inside sections/header.liquid, and
 * every cart mutation already re-renders + morphs that section through the
 * Section Rendering API (see assets/component-cart-items.js and
 * assets/product-form.js). That means the drawer upsell's trigger condition is
 * re-evaluated server-side in Liquid on every quantity change, removal and
 * add-to-cart for free. This module only handles accept/decline/tracking and
 * re-binds itself after each re-render via the theme's own event bus
 * (assets/events.js -> 'cart:update' / 'cart:add').
 *
 * No dependencies. No checkout-path code.
 */
(function () {
  'use strict';

  /* Guard against double-execution: theme.liquid loads this on every page,
     and snippets/block-upsell-card.liquid + block-upsell-upgrade.liquid ALSO
     emit their own <script src> tag for it so the block works standalone in
     the theme editor. A page that carries the upsell as BOTH a section and a
     theme block gets two <script> tags for the same src, and unlike a module,
     a classic script tag re-executes in full on each one -- registering the
     document/window-level listeners below (change, pageshow, DOMContentLoaded)
     a second time, so every accept/decline click and cart change would fire
     its handler twice. */
  if (window.__elnUpsellBooted) return;
  window.__elnUpsellBooted = true;

  var DECLINE_KEY = 'eln_upsell_declined:'; // + offerId + ':' + placement
  var TAKEN_KEY = 'eln_upsell_taken:'; // + offerId

  /* ------------------------------------------------------------------ *
   * session helpers
   * ------------------------------------------------------------------ */
  function safeGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function safeSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* no-op */
    }
  }

  function isDeclined(offerId, placement) {
    return safeGet(DECLINE_KEY + offerId + ':' + placement) === '1';
  }

  function isTaken(offerId) {
    return safeGet(TAKEN_KEY + offerId) === '1';
  }

  /* ------------------------------------------------------------------ *
   * tracking
   * ------------------------------------------------------------------ */
  function track(eventName, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    } catch (e) {
      /* no-op */
    }

    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (e) {
      /* no-op */
    }

    try {
      document.dispatchEvent(new CustomEvent('eln:' + eventName, { detail: payload }));
    } catch (e) {
      /* no-op */
    }
  }

  function payloadFrom(root) {
    return {
      offer_id: root.dataset.offerId,
      placement: root.dataset.placement,
      base_product: root.dataset.baseProduct,
      upgrade_product: root.dataset.upgradeProduct,
      delta: Number(root.dataset.delta || 0) / 100,
      cart_value: Number(root.dataset.cartValue || 0) / 100
    };
  }

  /* ------------------------------------------------------------------ *
   * visibility
   * ------------------------------------------------------------------ */
  function hide(root) {
    root.hidden = true;
    root.style.display = 'none';
  }

  function shouldSuppress(root) {
    var offerId = root.dataset.offerId;
    var placement = root.dataset.placement;

    if (isTaken(offerId)) return true;
    if (isDeclined(offerId, placement)) return true;

    if (placement === 'drawer' && root.dataset.pdpDeclineSuppresses === 'true') {
      if (isDeclined(offerId, 'product')) return true;
    }

    return false;
  }

  /* ------------------------------------------------------------------ *
   * cart swap / accept
   * ------------------------------------------------------------------ */
  function acceptOffer(root) {
    if (root.dataset.busy === '1') return;
    root.dataset.busy = '1';

    var acceptBtn = root.querySelector('[data-eln-upsell-accept]');
    if (acceptBtn) {
      acceptBtn.setAttribute('aria-busy', 'true');
      acceptBtn.disabled = true;
      acceptBtn.classList.add('eln-upsell__btn--loading');
    }

    var baseVariant = root.dataset.triggerVariant;
    var upgradeVariant = root.dataset.upgradeVariant;
    var qty = Number(root.dataset.triggerQty || 1) || 1;

    var updates = {};
    updates[baseVariant] = 0;
    updates[upgradeVariant] = qty;

    var body = {
      updates: updates,
      sections: sectionsToRequest(),
      sections_url: window.location.pathname
    };

    fetch((window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart update failed: ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        safeSet(TAKEN_KEY + root.dataset.offerId, '1');
        track('upsell_accepted', payloadFrom(root));
        notifyThemeOfCartChange(cart);
        hide(root);
      })
      .catch(function (error) {
        console.error('[eln-upsell]', error);
        restoreAfterFailure(root, acceptBtn);
      });
  }

  function restoreAfterFailure(root, acceptBtn) {
    root.dataset.busy = '0';

    if (acceptBtn) {
      acceptBtn.removeAttribute('aria-busy');
      acceptBtn.disabled = false;
      acceptBtn.classList.remove('eln-upsell__btn--loading');
    }

    var errorEl = root.querySelector('[data-eln-upsell-error]');
    if (errorEl) {
      errorEl.hidden = false;
      errorEl.textContent = root.dataset.errorText || 'تعذّر تحديث السلة، حاولي مرة أخرى.';
    }

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        notifyThemeOfCartChange(cart);
      })
      .catch(function () {
        /* offline */
      });
  }

  function sectionsToRequest() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids.join(',');
  }

  function notifyThemeOfCartChange(cart) {
    var detail = { cart: cart, source: 'eln-upsell' };

    try {
      document.dispatchEvent(new CustomEvent('cart:update', { detail: detail, bubbles: true }));
    } catch (e) {
      /* no-op */
    }

    var sections = sectionsToRequest();
    if (!sections) return;

    fetch(window.location.pathname + '?sections=' + encodeURIComponent(sections), {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        Object.keys(data).forEach(function (sectionId) {
          var target = document.querySelector('[data-section-id="' + sectionId + '"]');
          if (!target) return;
          var parsed = new DOMParser().parseFromString(data[sectionId], 'text/html');
          var fresh = parsed.querySelector('[data-section-id="' + sectionId + '"]');
          if (fresh) target.replaceWith(fresh);
        });
        bindAll();
      })
      .catch(function () {
        /* leave theme rendering */
      });
  }

  /* ------------------------------------------------------------------ *
   * decline
   * ------------------------------------------------------------------ */
  function declineOffer(root) {
    safeSet(DECLINE_KEY + root.dataset.offerId + ':' + root.dataset.placement, '1');
    track('upsell_declined', payloadFrom(root));
    hide(root);
  }

  /* ------------------------------------------------------------------ *
   * product-page placement
   * ------------------------------------------------------------------ */
  function parseExtraVariants(input) {
    if (!input || !input.dataset.extraVariants) return [];
    return input.dataset.extraVariants
      .split(',')
      .map(function (v) {
        return v.trim();
      })
      .filter(Boolean);
  }

  function addBundleToCart(event, form, mainVariant, extraVariants) {
    event.preventDefault();
    event.stopImmediatePropagation();

    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    var qtyInput = form.querySelector('input[name="quantity"]');
    var qty = qtyInput ? Number(qtyInput.value) || 1 : 1;

    var items = [{ id: Number(mainVariant), quantity: qty }];
    extraVariants.forEach(function (id) {
      items.push({ id: Number(id), quantity: 1 });
    });

    var body = {
      items: items,
      sections: sectionsToRequest(),
      sections_url: window.location.pathname
    };

    fetch((window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        if (!response.ok) return response.json().then(function (err) { throw new Error(err.description || 'cart add failed'); });
        return fetch('/cart.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (submitBtn) submitBtn.disabled = false;
        notifyThemeOfCartChange(cart);
        track('eln_bundle_added', { main_variant: mainVariant, extra_variants: extraVariants.join(',') });
      })
      .catch(function (error) {
        console.error('[eln-upsell]', error);
        if (submitBtn) submitBtn.disabled = false;
      });
  }

  function bindProductChoice(root) {
    if (root.dataset.choiceBound === '1') return;
    root.dataset.choiceBound = '1';

    /*
     * Which forms this widget is allowed to rewrite.
     *
     * This used to be a bare document.querySelectorAll over EVERY cart-add
     * form on the page, which is only correct on a product page where the
     * single main form belongs to the product being upsold. On any page that
     * also lists other products it was actively destructive: the upsell
     * stamped its own trigger variant into every product card's form, so all
     * eight cards on the home page posted the Shib variant (54199366844729)
     * no matter which product the customer clicked. Reproduced live before
     * this change -- eight different products, one variant id.
     *
     * A product card carries its own variant and must never be touched. The
     * legitimate target is the main product form, which is never inside a
     * card. On the Shib landing page that still resolves to .ph-buy-form in
     * the main section (the upsell sits in its own section, so scoping by
     * section would have broken the case this feature exists for); on the
     * home page it resolves to nothing and the widget correctly no-ops,
     * adding its own variant through the fetch path instead.
     */
    var CARD_SCOPE =
      '.bb-pc__card, .card, product-card, quick-add-modal, .quick-add, ' +
      '[data-eln-standalone-card], [data-eln-choice-card]';

    function targetForms() {
      // Inside a form: that form is unambiguously the one being configured.
      var own = root.closest('form[action*="/cart/add"], [data-ph-buy-form]');
      if (own) return [own];

      return Array.prototype.filter.call(
        document.querySelectorAll('form[action*="/cart/add"], [data-ph-buy-form]'),
        function (form) {
          return !form.closest(CARD_SCOPE);
        }
      );
    }

    function syncVariantToForm(variantId) {
      if (!variantId) return;
      targetForms().forEach(function (form) {
        var idField = form.querySelector('input[name="id"]');
        if (idField && idField.value !== String(variantId)) {
          idField.value = String(variantId);
          idField.dispatchEvent(new Event('change', { bubbles: true }));
          idField.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
    }

    function paint() {
      root.querySelectorAll('[data-eln-choice]').forEach(function (input) {
        var card = input.closest('[data-eln-choice-card]');
        if (card) {
          card.classList.toggle('eln-upsell__card--active', Boolean(input.checked));
          card.dataset.wasChecked = input.checked ? 'true' : 'false';
        }
      });

      var checkedInput = root.querySelector('[data-eln-choice]:checked');
      if (checkedInput) {
        syncVariantToForm(checkedInput.value);
      } else if (root.dataset.triggerVariant) {
        syncVariantToForm(root.dataset.triggerVariant);
      }
    }

    // Toggle support: clicking an already selected card deselects it
    root.addEventListener('pointerdown', function (event) {
      var card = event.target.closest('[data-eln-choice-card]');
      if (!card) return;
      var input = card.querySelector('[data-eln-choice]');
      if (input) {
        card.dataset.wasChecked = input.checked ? 'true' : 'false';
      }
    });

    root.addEventListener('click', function (event) {
      var card = event.target.closest('[data-eln-choice-card]');
      if (!card) return;
      var input = card.querySelector('[data-eln-choice]');
      if (!input) return;

      if (card.dataset.wasChecked === 'true') {
        event.preventDefault();
        input.checked = false;
        card.dataset.wasChecked = 'false';
        root.dataset.userChose = '1';
        paint();

        var priceSelector = root.dataset.priceSelector;
        if (priceSelector && root.dataset.basePriceText) {
          var priceEl = document.querySelector(priceSelector);
          if (priceEl) priceEl.textContent = root.dataset.basePriceText;
        }
        if (root.dataset.triggerVariant) {
          syncVariantToForm(root.dataset.triggerVariant);
        }
      }
    });

    function enforceServerChoice() {
      if (root.dataset.userChose === '1') return;
      var serverChecked = root.querySelector('[data-eln-choice][checked]');
      if (serverChecked && !serverChecked.checked) {
        serverChecked.checked = true;
        paint();
      }
    }

    enforceServerChoice();
    setTimeout(enforceServerChoice, 0);
    window.addEventListener('pageshow', enforceServerChoice);

    root.addEventListener('change', function (event) {
      if (!event.target.matches('[data-eln-choice]')) return;
      root.dataset.userChose = '1';
      paint();

      var priceSelector = root.dataset.priceSelector;
      if (priceSelector && event.target.dataset.priceText) {
        var priceEl = document.querySelector(priceSelector);
        if (priceEl) priceEl.textContent = event.target.dataset.priceText;
      }
      if (event.target.value) {
        syncVariantToForm(event.target.value);
      }
    });

    paint();
  }

  /* ------------------------------------------------------------------ *
   * choices carousel -- sliding with arrows when 3+ cards exist
   * ------------------------------------------------------------------ */
  function bindChoicesCarousel(wrap) {
    if (!wrap) return;
    wrap.dataset.carouselBound = '1';

    var track = wrap.querySelector('.eln-upsell__choices');
    var prevBtn = wrap.querySelector('[data-eln-choices-prev]');
    var nextBtn = wrap.querySelector('[data-eln-choices-next]');
    if (!track || !prevBtn || !nextBtn) return;

    function isRtlMode() {
      var dir = track.getAttribute('dir') || getComputedStyle(track).direction || document.documentElement.getAttribute('dir') || 'rtl';
      return dir === 'rtl';
    }

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      if (max <= 8) {
        prevBtn.hidden = true;
        nextBtn.hidden = true;
        return;
      }

      var scrollLeft = track.scrollLeft;
      var absPos = Math.abs(scrollLeft);

      if (absPos <= 8) {
        prevBtn.hidden = true;
        nextBtn.hidden = false;
      } else if (absPos >= max - 8) {
        prevBtn.hidden = false;
        nextBtn.hidden = true;
      } else {
        prevBtn.hidden = false;
        nextBtn.hidden = false;
      }
    }

    function step(direction) {
      var isRtl = isRtlMode();
      var card = track.querySelector('.eln-upsell__card');
      var amount = card ? card.getBoundingClientRect().width + 14 : track.clientWidth * 0.85;
      var sign = isRtl ? -1 : 1;
      track.scrollBy({ left: sign * direction * amount, behavior: 'smooth' });
      setTimeout(update, 350);
    }

    prevBtn.onclick = function (e) {
      e.preventDefault();
      step(-1);
    };

    nextBtn.onclick = function (e) {
      e.preventDefault();
      step(1);
    };

    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    /* The first arrow check reads scrollWidth and clientWidth, which makes the
       browser lay the page out on the spot. Done during DOMContentLoaded that
       was a forced reflow on the critical path. A frame later it happens as
       part of the normal render instead, and the arrows are only hidden or
       shown a frame after first paint. */
    if (window.requestAnimationFrame) window.requestAnimationFrame(update);
    else update();
    setTimeout(update, 150);
    setTimeout(update, 500);
  }

  /* ------------------------------------------------------------------ *
   * binding
   * ------------------------------------------------------------------ */
  function bindOne(root) {
    if (shouldSuppress(root)) {
      hide(root);
      return;
    }

    if (root.dataset.placement === 'product') {
      bindProductChoice(root);
      bindChoicesCarousel(root.querySelector('[data-eln-choices-wrap]'));
    }

    if (root.dataset.bound !== '1') {
      root.dataset.bound = '1';

      var accept = root.querySelector('[data-eln-upsell-accept]');
      if (accept && root.dataset.placement === 'drawer') {
        accept.addEventListener('click', function (event) {
          event.preventDefault();
          acceptOffer(root);
        });
      }

      var decline = root.querySelector('[data-eln-upsell-decline]');
      if (decline) {
        decline.addEventListener('click', function (event) {
          event.preventDefault();
          declineOffer(root);
        });
      }
    }

    if (root.dataset.shown !== '1') {
      root.dataset.shown = '1';
      track('upsell_shown', payloadFrom(root));
    }
  }

  function bindAll() {
    document.querySelectorAll('[data-eln-upsell]').forEach(bindOne);
    document.querySelectorAll('[data-eln-choices-wrap]').forEach(bindChoicesCarousel);
  }

  /* ------------------------------------------------------------------ *
   * standalone option-card sections
   * ------------------------------------------------------------------ */
  var STANDALONE_GROUP = 'eln-upsell-choice-standalone';
  var standaloneBooted = false;

  function standaloneRadios() {
    return document.querySelectorAll('[data-eln-choice][name="' + STANDALONE_GROUP + '"]');
  }

  function standaloneDefaultVariant() {
    var el = document.querySelector('[data-eln-standalone-card]');
    return el ? el.dataset.defaultVariant : null;
  }

  function paintStandalone() {
    standaloneRadios().forEach(function (input) {
      var card = input.closest('[data-eln-choice-card]');
      if (card) card.classList.toggle('eln-upsell__card--active', input.checked);
    });
  }

  function selectedStandaloneInput() {
    return document.querySelector('[data-eln-choice][name="' + STANDALONE_GROUP + '"]:checked');
  }

  function assembleStandaloneCarousel() {
    var looseCards = document.querySelectorAll('[data-eln-standalone-card]');
    if (!looseCards.length) return;

    var wrap = document.querySelector('[data-eln-standalone-carousel]');
    var track;

    if (!wrap) {
      if (looseCards.length < 2) return;

      var anchor = looseCards[0];
      var parent = anchor.parentNode;
      if (!parent) return;

      var first = looseCards[0];
      var panel = document.createElement('div');
      panel.className = 'eln-upsell eln-upsell--product';
      panel.setAttribute('data-eln-standalone-panel', '');

      var panelStyle = first.dataset.groupPanelStyle;
      if (panelStyle) panel.style.cssText = panelStyle;

      var badgeText = first.dataset.groupBadge;
      if (badgeText) {
        var badgeEl = document.createElement('span');
        badgeEl.className = 'eln-upsell__badge';
        badgeEl.textContent = badgeText;
        panel.appendChild(badgeEl);
      }

      var headlineText = first.dataset.groupHeadline;
      if (headlineText) {
        var headlineEl = document.createElement('p');
        headlineEl.className = 'eln-upsell__headline';
        headlineEl.textContent = headlineText;
        panel.appendChild(headlineEl);
      }

      var bodyText = first.dataset.groupBody;
      if (bodyText) {
        var bodyEl = document.createElement('p');
        bodyEl.className = 'eln-upsell__body';
        bodyEl.textContent = bodyText;
        panel.appendChild(bodyEl);
      }

      wrap = document.createElement('div');
      wrap.className = 'eln-upsell__choices-wrap';
      wrap.setAttribute('data-eln-choices-wrap', '');
      wrap.setAttribute('data-eln-standalone-carousel', '');

      var prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'eln-upsell__choices-arrow eln-upsell__choices-arrow--prev';
      prevBtn.setAttribute('data-eln-choices-prev', '');
      prevBtn.setAttribute('aria-label', 'السابق');
      prevBtn.hidden = true;
      prevBtn.innerHTML =
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';

      var nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'eln-upsell__choices-arrow eln-upsell__choices-arrow--next';
      nextBtn.setAttribute('data-eln-choices-next', '');
      nextBtn.setAttribute('aria-label', 'التالي');
      nextBtn.hidden = true;
      nextBtn.innerHTML =
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';

      track = document.createElement('div');
      track.className = 'eln-upsell__choices';

      wrap.appendChild(prevBtn);
      wrap.appendChild(track);
      wrap.appendChild(nextBtn);
      panel.appendChild(wrap);

      var declineText = first.dataset.groupDecline;
      if (declineText) {
        var declineBtn = document.createElement('button');
        declineBtn.type = 'button';
        declineBtn.className = 'eln-upsell__btn eln-upsell__btn--decline';
        declineBtn.textContent = declineText;
        declineBtn.addEventListener('click', function () {
          panel.hidden = true;
        });
        panel.appendChild(declineBtn);
      }

      parent.insertBefore(panel, anchor);
    } else {
      track = wrap.querySelector('.eln-upsell__choices');
    }

    if (!track) return;

    looseCards.forEach(function (cardWrapper) {
      var cardEl = cardWrapper.querySelector('.eln-upsell__card');
      if (cardEl) track.appendChild(cardEl);
      cardWrapper.remove();
    });

    bindChoicesCarousel(wrap);
    window.dispatchEvent(new Event('resize'));
  }

  function bindStandaloneCards() {
    assembleStandaloneCarousel();
    paintStandalone();
    if (standaloneBooted) return;
    standaloneBooted = true;

    document.addEventListener('change', function (event) {
      if (!event.target.matches('[data-eln-choice][name="' + STANDALONE_GROUP + '"]')) return;
      paintStandalone();
    });

  }

  /* ------------------------------------------------------------------ *
   * checkout flag
   * ------------------------------------------------------------------ */
  function bindCheckoutFlag() {
    document.addEventListener(
      'click',
      function (event) {
        var target =
          event.target instanceof Element
            ? event.target.closest('[name="checkout"], [href*="/checkout"]')
            : null;
        if (!target) return;

        var shown = document.querySelectorAll('[data-eln-upsell]:not([hidden])');
        var placements = [];
        shown.forEach(function (el) {
          placements.push(el.dataset.placement);
        });

        track('checkout_started', {
          upsell_shown: placements.length > 0,
          upsell_placements: placements.join(',') || 'none'
        });
      },
      true
    );
  }

  /* ------------------------------------------------------------------ *
   * boot & re-renders
   * ------------------------------------------------------------------ */
  function boot() {
    bindAll();
    bindCheckoutFlag();
    bindStandaloneCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  ['cart:update', 'cart:add'].forEach(function (name) {
    document.addEventListener(name, function () {
      setTimeout(bindAll, 0);
    });
  });

  document.addEventListener('shopify:section:load', function () {
    bindAll();
    assembleStandaloneCarousel();
    paintStandalone();
  });

  document.addEventListener('shopify:block:select', function () {
    bindAll();
    paintStandalone();
  });

  window.ElnUpsell = { bindAll: bindAll, track: track };
})();
